import {
  APY_RANGE_KEY,
  INCLUDE_REWARDS_KEY,
  MIN_VAULT_SCORE_KEY,
  ONLY_TRANSACTIONAL_KEY,
  PAGE_KEY,
  SEARCH_FOR_KEY,
  SELECTED_CURATORS_KEY,
  SELECTED_NETWORKS_KEY,
  SELECTED_PROTOCOLS_KEY,
  SELECTED_TAGS_KEY,
  SELECTED_TOKENS_KEY,
  TVL_FROM_BOUND_KEY,
  TVL_TO_BOUND_KEY,
} from '@/constants/state'
import { DEFAULT_SETTINGS, VALID_APY_RANGES, useSettingsContext } from '@/context/settings'
import type { VaultApyRange } from '@/types/apy'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { filterNetworks } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { useSearchState } from './useSearchState'

const DEFAULT_SEARCH_STATE = {
  page: 1,
  searchFor: '',
  apyRange: DEFAULT_SETTINGS.apyRange,
  includeRewards: DEFAULT_SETTINGS.includeRewards,
  tvlRange: DEFAULT_SETTINGS.tvlRange,
  chartsPeriod: DEFAULT_SETTINGS.chartsPeriod,
} as const

export function useVaultsSearchState() {
  const { apyRange: apyRangeSetting, tvlRange, includeRewards: includeRewardsSetting } = useSettingsContext()
  const { searchState, touchedParamsCount, updateSearchState, resetSearchParam, clearSearchState } = useSearchState({
    debounceTimeout: SECOND_IN_MILLISECONDS / 2,
    debouncedKeys: [
      SEARCH_FOR_KEY,
      APY_RANGE_KEY,
      INCLUDE_REWARDS_KEY,
      ONLY_TRANSACTIONAL_KEY,
      SELECTED_NETWORKS_KEY,
      SELECTED_PROTOCOLS_KEY,
      SELECTED_CURATORS_KEY,
      SELECTED_TAGS_KEY,
      SELECTED_TOKENS_KEY,
      TVL_FROM_BOUND_KEY,
      TVL_TO_BOUND_KEY,
      MIN_VAULT_SCORE_KEY,
    ],
  })

  const page = Number(searchState.get(PAGE_KEY) ?? DEFAULT_SEARCH_STATE.page)
  const searchFor = searchState.get(SEARCH_FOR_KEY) ?? DEFAULT_SEARCH_STATE.searchFor

  const selectedNetworks = filterNetworks(searchState.getAll(SELECTED_NETWORKS_KEY))
  const selectedProtocols = searchState.getAll(SELECTED_PROTOCOLS_KEY)
  const selectedCurators = searchState.getAll(SELECTED_CURATORS_KEY)
  const selectedTags = searchState.getAll(SELECTED_TAGS_KEY)
  const selectedTokens = searchState.getAll(SELECTED_TOKENS_KEY)

  const selectedTvlFrom = Number(searchState.get(TVL_FROM_BOUND_KEY) ?? tvlRange[0] ?? DEFAULT_SEARCH_STATE.tvlRange[0])
  const selectedTvlTo = Number(searchState.get(TVL_TO_BOUND_KEY) ?? tvlRange[1] ?? DEFAULT_SEARCH_STATE.tvlRange[1])
  const selectedTvlRange: [number, number] = useMemo(
    () => [selectedTvlFrom, selectedTvlTo],
    [selectedTvlFrom, selectedTvlTo]
  )
  const rewardsSearchParam = searchState.get(INCLUDE_REWARDS_KEY)
  const includeRewards =
    rewardsSearchParam === 'false'
      ? false
      : rewardsSearchParam === 'true'
        ? true
        : (includeRewardsSetting ?? DEFAULT_SEARCH_STATE.includeRewards)

  const apyRangeSearchParam = searchState.get(APY_RANGE_KEY)
  const apyRange: VaultApyRange =
    apyRangeSearchParam && VALID_APY_RANGES.includes(apyRangeSearchParam as VaultApyRange)
      ? (apyRangeSearchParam as VaultApyRange)
      : (apyRangeSetting ?? DEFAULT_SEARCH_STATE.apyRange)

  const onlyTransactional = searchState.get(ONLY_TRANSACTIONAL_KEY) === 'true'

  const minVaultScore = Number(searchState.get(MIN_VAULT_SCORE_KEY) ?? 0)

  return {
    searchState,
    touchedParamsCount,
    updateSearchState,
    clearSearchState,
    resetSearchParam,
    page,
    searchFor,
    selectedNetworks,
    selectedProtocols,
    selectedCurators,
    selectedTags,
    selectedTokens,
    selectedTvlRange,
    includeRewards,
    apyRange,
    onlyTransactional,
    minVaultScore,
  } as const
}
